import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Switch,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import HearingIcon from "@material-ui/icons/Hearing";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import EditIcon from "@material-ui/icons/Edit";
import {
  createListening,
  deleteListening,
  getListeningComponents,
  getListeningDetailed,
  getListenings,
  updateListening,
} from "../utils/NetworkHandler";
import ListeningListView from "./ListeningListView";
import ListeningDetailedView from "./ListeningDetailedView";
import ListeningComponentsView from "./ListeningComponentsView";
import ListeningFormView from "./ListeningFormView";
import useListeningBrowser from "./useListeningBrowser";
import useListeningForm from "./useListeningForm";
import {
  buildCreateRequestBody,
  buildUpdateRequestBody,
  getDefaultSelectedFrequencies,
  getListeningDetailsDialogTitle,
  getListeningsDialogTitle,
  normalizeAvailableFrequencies,
} from "./listeningFormUtils";
import { LISTENING_DIALOG_MODES, LISTENING_FORM_STEPS } from "./listeningModes";
import { LISTENING_DISPLAY_UNITS } from "./listeningUnits";

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  );
}

export default function ListeningDialog({
  open,
  onClose,
  ownerCalibrationId,
  averagingLevel,
  defaultDeviceId,
  defaultModelName,
  defaultBrandName,
  availableFrequencies = [],
  onListeningCreated,
  onListeningDeleted,
  listeningApi,
  modeLabel,
  isBluetoothSpeaker = false,
}) {
  const normalizedAvailableFrequencies = useMemo(
    () => normalizeAvailableFrequencies(availableFrequencies),
    [availableFrequencies],
  );
  const defaultSelectedFrequencies = useMemo(
    () =>
      getDefaultSelectedFrequencies(
        normalizedAvailableFrequencies,
        isBluetoothSpeaker,
      ),
    [normalizedAvailableFrequencies, isBluetoothSpeaker],
  );

  const effectiveListeningApi = useMemo(
    () => ({
      createListening,
      getListenings,
      getListeningDetailed,
      getListeningComponents,
      updateListening,
      deleteListening,
      ...(listeningApi || {}),
    }),
    [listeningApi],
  );

  const isByModel = averagingLevel === "BY_MODEL";
  const effectiveModeLabel =
    modeLabel || (isByModel ? "BY_MODEL" : "BY_DEVICE_ID");

  const [mode, setMode] = useState(LISTENING_DIALOG_MODES.LIST);
  const defaultDisplayUnit = isBluetoothSpeaker
    ? LISTENING_DISPLAY_UNITS.SPL
    : LISTENING_DISPLAY_UNITS.HL;
  const [displayUnit, setDisplayUnit] = useState(defaultDisplayUnit);

  const {
    listenings,
    listLoading,
    listError,
    selectedListeningId,
    detailedListening,
    detailedLoading,
    detailedError,
    setDetailedError,
    componentsData,
    componentsLoading,
    componentsError,
    setComponentsError,
    loadListenings,
    loadDetailedListening,
    loadListeningComponents,
    handleDeleteListening,
  } = useListeningBrowser({
    open,
    ownerCalibrationId,
    onListeningDeleted,
    listeningApi: effectiveListeningApi,
  });

  const {
    form,
    step,
    validationErrors,
    fieldErrors,
    submitError,
    setSubmitError,
    submitting,
    setSubmitting,
    resetCreateState,
    resetEditState,
    loadEditForm,
    runValidation,
    handleFieldChange,
    toggleFrequency,
    toggleIntensity,
    handleNoStimulusChange,
    handleTableValueChange,
    goToValuesStep,
    goBackToDeclarationStep,
    editAvailableFrequencies,
  } = useListeningForm({
    normalizedAvailableFrequencies,
    defaultSelectedFrequencies,
    defaultDeviceId,
    defaultModelName,
    isByModel,
  });

  const openListMode = useCallback(() => {
    setMode(LISTENING_DIALOG_MODES.LIST);
  }, []);

  const openCreateMode = useCallback(() => {
    resetCreateState();
    setMode(LISTENING_DIALOG_MODES.CREATE);
  }, [resetCreateState]);

  const openDetailsMode = useCallback(() => {
    setMode(LISTENING_DIALOG_MODES.DETAILS);
  }, []);

  const openComponentsMode = useCallback(() => {
    setMode(LISTENING_DIALOG_MODES.COMPONENTS);
  }, []);

  const openEditMode = useCallback(() => {
    if (!detailedListening) {
      return;
    }

    loadEditForm(detailedListening);
    setMode(LISTENING_DIALOG_MODES.EDIT);
  }, [detailedListening, loadEditForm]);

  useEffect(() => {
    if (!open) {
      return;
    }

    openListMode();
    resetCreateState();
  }, [
    open,
    ownerCalibrationId,
    averagingLevel,
    defaultDeviceId,
    defaultModelName,
    normalizedAvailableFrequencies,
    openListMode,
    resetCreateState,
  ]);

  const handleCreateSubmit = useCallback(async () => {
    const validationResult = runValidation(form, isByModel);

    if (!validationResult.isValid) {
      setSubmitError("Uzupełnij wymagane pola w formularzu.");
      return;
    }

    if (!ownerCalibrationId) {
      setSubmitError("Brakuje ownerCalibrationId.");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const body = buildCreateRequestBody({
        form,
        averagingLevel,
        defaultModelName,
      });

      const response = await effectiveListeningApi.createListening(
        ownerCalibrationId,
        body,
      );

      if (onListeningCreated) {
        Promise.resolve(onListeningCreated(response?.data)).catch((error) => {
          console.error("onListeningCreated failed", error);
        });
      }

      await loadListenings();
      resetCreateState();
      openListMode();
    } catch (error) {
      console.error("Failed to create listening", error);
      setSubmitError(getErrorMessage(error, "Nie udało się dodać odsłuchu."));
    } finally {
      setSubmitting(false);
    }
  }, [
    form,
    isByModel,
    ownerCalibrationId,
    averagingLevel,
    defaultModelName,
    onListeningCreated,
    loadListenings,
    resetCreateState,
    runValidation,
    openListMode,
    setSubmitError,
    setSubmitting,
    effectiveListeningApi,
  ]);

  const handleUpdateSubmit = useCallback(async () => {
    const editByModel = form?.isByModel === true;
    const validationResult = runValidation(form, editByModel);

    if (!validationResult.isValid) {
      setSubmitError("Uzupełnij wymagane pola w formularzu.");
      return;
    }

    const listeningId = detailedListening?.id || selectedListeningId;
    if (!listeningId) {
      setSubmitError("Brakuje listeningId.");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const body = buildUpdateRequestBody({
        form,
        defaultModelName,
      });

      await effectiveListeningApi.updateListening(listeningId, body);
      await loadListenings();
      await loadDetailedListening(listeningId);
      resetEditState();
      openDetailsMode();
    } catch (error) {
      console.error("Failed to update listening", error);
      setSubmitError(
        getErrorMessage(error, "Nie udało się zaktualizować odsłuchu."),
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    form,
    detailedListening,
    selectedListeningId,
    defaultModelName,
    loadDetailedListening,
    loadListenings,
    resetEditState,
    runValidation,
    openDetailsMode,
    setSubmitError,
    setSubmitting,
    effectiveListeningApi,
  ]);

  const handleOpenDetails = useCallback(
    async (listeningId) => {
      setDisplayUnit(defaultDisplayUnit);
      openDetailsMode();
      await loadDetailedListening(listeningId);
    },
    [defaultDisplayUnit, loadDetailedListening, openDetailsMode],
  );

  const handleOpenComponents = useCallback(async () => {
    openComponentsMode();
    await loadListeningComponents();
  }, [loadListeningComponents, openComponentsMode]);

  const handleClose = useCallback(
    (event, reason) => {
      const isFormMode =
        mode === LISTENING_DIALOG_MODES.CREATE ||
        mode === LISTENING_DIALOG_MODES.EDIT;

      if (reason === "backdropClick" && isFormMode) {
        return;
      }

      onClose();
    },
    [mode, onClose],
  );

  const renderListMode = () => (
    <>
      <DialogTitle>
        {getListeningsDialogTitle({
          isByModel,
          defaultBrandName,
          defaultModelName,
          defaultDeviceId,
        })}
      </DialogTitle>

      <ListeningListView
        listenings={listenings}
        loading={listLoading}
        error={listError}
        isByModel={isByModel}
        onOpenDetails={handleOpenDetails}
        onDeleteListening={handleDeleteListening}
      />

      <DialogActions>
        <Button onClick={handleClose}>Zamknij</Button>

        <Button
          startIcon={<RefreshIcon />}
          onClick={loadListenings}
          disabled={listLoading}
        >
          Odśwież
        </Button>

        <Button
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateMode}
        >
          dodaj odsłuch
        </Button>
      </DialogActions>
    </>
  );

  const renderCreateMode = () => (
    <>
      <DialogTitle>
        {step === LISTENING_FORM_STEPS.DECLARATION
          ? "Dodawanie odsłuchu - krok 1/2"
          : "Dodawanie odsłuchu - krok 2/2"}
      </DialogTitle>

      <ListeningFormView
        step={step}
        modeLabel={effectiveModeLabel}
        modeIcon={HearingIcon}
        form={form}
        validationErrors={validationErrors}
        fieldErrors={fieldErrors}
        availableFrequencies={normalizedAvailableFrequencies}
        onFieldChange={handleFieldChange}
        onToggleFrequency={toggleFrequency}
        onToggleIntensity={toggleIntensity}
        onNoStimulusChange={handleNoStimulusChange}
        onTableValueChange={handleTableValueChange}
        showDeviceIdField={isByModel}
        isByModel={isByModel}
        submitError={submitError}
      />

      <DialogActions>
        <Button onClick={handleClose}>Zamknij</Button>

        {step === LISTENING_FORM_STEPS.DECLARATION ? (
          <>
            <Button
              onClick={() => {
                resetCreateState();
                openListMode();
              }}
            >
              Wstecz
            </Button>

            <Button
              color="primary"
              variant="contained"
              onClick={() => goToValuesStep(isByModel)}
            >
              Dalej
            </Button>
          </>
        ) : (
          <>
            <Button onClick={goBackToDeclarationStep}>Wstecz</Button>

            <Button
              color="primary"
              variant="contained"
              onClick={handleCreateSubmit}
              disabled={submitting}
            >
              {submitting ? "Zapisywanie..." : "Zapisz"}
            </Button>
          </>
        )}
      </DialogActions>
    </>
  );

  const renderEditMode = () => (
    <>
      <DialogTitle>
        {step === LISTENING_FORM_STEPS.DECLARATION
          ? "Edycja odsłuchu - krok 1/2"
          : "Edycja odsłuchu - krok 2/2"}
      </DialogTitle>

      <ListeningFormView
        step={step}
        modeLabel={effectiveModeLabel}
        modeIcon={EditIcon}
        form={form}
        validationErrors={validationErrors}
        fieldErrors={fieldErrors}
        availableFrequencies={editAvailableFrequencies}
        onFieldChange={handleFieldChange}
        onToggleFrequency={toggleFrequency}
        onToggleIntensity={toggleIntensity}
        onNoStimulusChange={handleNoStimulusChange}
        onTableValueChange={handleTableValueChange}
        showDeviceIdField={form?.isByModel === true}
        isByModel={form?.isByModel === true}
        submitError={submitError}
      />

      <DialogActions>
        <Button onClick={handleClose}>Zamknij</Button>

        {step === LISTENING_FORM_STEPS.DECLARATION ? (
          <>
            <Button
              onClick={() => {
                resetEditState();
                openDetailsMode();
              }}
            >
              Wstecz
            </Button>

            <Button
              color="primary"
              variant="contained"
              onClick={() => goToValuesStep(form?.isByModel === true)}
            >
              Dalej
            </Button>
          </>
        ) : (
          <>
            <Button onClick={goBackToDeclarationStep}>Wstecz</Button>

            <Button
              color="primary"
              variant="contained"
              onClick={handleUpdateSubmit}
              disabled={submitting}
            >
              {submitting ? "Zapisywanie..." : "Zapisz"}
            </Button>
          </>
        )}
      </DialogActions>
    </>
  );

  const renderDetailsMode = () => (
    <>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>
            {getListeningDetailsDialogTitle({
              defaultBrandName,
              defaultModelName,
              listening: detailedListening,
            })}
          </span>
          {!isBluetoothSpeaker && (
            <Box display="flex" alignItems="center" ml={2}>
              <Typography variant="body2">SPL</Typography>
              <Switch
                checked={displayUnit === LISTENING_DISPLAY_UNITS.HL}
                onChange={(event) =>
                  setDisplayUnit(
                    event.target.checked
                      ? LISTENING_DISPLAY_UNITS.HL
                      : LISTENING_DISPLAY_UNITS.SPL,
                  )
                }
                color="primary"
                inputProps={{ "aria-label": "Jednostka wyświetlania SPL lub HL" }}
              />
              <Typography variant="body2">HL</Typography>
            </Box>
          )}
        </Box>
      </DialogTitle>

      <ListeningDetailedView
        listening={detailedListening}
        loading={detailedLoading}
        error={detailedError}
        isByModel={isByModel}
        onOpenComponents={handleOpenComponents}
        displayUnit={displayUnit}
        isBluetoothSpeaker={isBluetoothSpeaker}
      />

      <DialogActions>
        <Button onClick={handleClose}>Zamknij</Button>

        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            openListMode();
            setDetailedError(null);
          }}
        >
          Wstecz
        </Button>

        <Button
          color="primary"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={openEditMode}
          disabled={!detailedListening?.id}
        >
          Edytuj
        </Button>
      </DialogActions>
    </>
  );

  const renderComponentsMode = () => (
    <>
      <DialogTitle>Składowe kalibracje odsłuchu</DialogTitle>

      <ListeningComponentsView
        data={componentsData}
        loading={componentsLoading}
        error={componentsError}
      />

      <DialogActions>
        <Button onClick={handleClose}>Zamknij</Button>

        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            openDetailsMode();
            setComponentsError(null);
          }}
        >
          Wstecz
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      {mode === LISTENING_DIALOG_MODES.LIST && renderListMode()}
      {mode === LISTENING_DIALOG_MODES.CREATE && renderCreateMode()}
      {mode === LISTENING_DIALOG_MODES.EDIT && renderEditMode()}
      {mode === LISTENING_DIALOG_MODES.DETAILS && renderDetailsMode()}
      {mode === LISTENING_DIALOG_MODES.COMPONENTS && renderComponentsMode()}
    </Dialog>
  );
}
